import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLocalCategoriesList } from 'entities/Category/model/selectors/categorySelectors';
import {
    type CustomListItem,
    getCustomListCatalogItem,
} from 'entities/CustomList';

import { customListItemMatchesSearch } from '../../lib/customListItemMatchesSearch';

export type OrderAddItemsListsSortKey =
    | 'item'
    | 'category'
    | 'uom'
    | 'defaultQty';

const compareText = (left: string, right: string) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' });

const filterAndSortListItems = (
    items: CustomListItem[],
    itemSearch: string,
    categoryFilter: string,
    sortKey: OrderAddItemsListsSortKey,
    sortDir: 'asc' | 'desc',
    categories: ReturnType<typeof getLocalCategoriesList>,
) => {
    const query = itemSearch.trim().toLowerCase();
    const filtered = items.filter((listItem) => {
        if (!customListItemMatchesSearch(listItem, query)) {
            return false;
        }
        if (categoryFilter === 'all') {
            return true;
        }
        return (
            getCustomListCatalogItem(listItem)?.categoryId === categoryFilter
        );
    });

    const dir = sortDir === 'asc' ? 1 : -1;

    return [...filtered].sort((a, b) => {
        const itemA = getCustomListCatalogItem(a);
        const itemB = getCustomListCatalogItem(b);

        const categoryNameA =
            categories?.[itemA?.categoryId || '']?.title || '';
        const categoryNameB =
            categories?.[itemB?.categoryId || '']?.title || '';
        const titleA = itemA?.title || '';
        const titleB = itemB?.title || '';

        if (sortKey === 'category') {
            const byCategory = compareText(categoryNameA, categoryNameB);
            if (byCategory !== 0) {
                return byCategory * dir;
            }
            return compareText(titleA, titleB) * dir;
        }

        if (sortKey === 'item') {
            return compareText(titleA, titleB) * dir;
        }

        if (sortKey === 'uom') {
            return compareText(itemA?.uom || '', itemB?.uom || '') * dir;
        }

        if (sortKey === 'defaultQty') {
            return ((a.targetQuantity || 1) - (b.targetQuantity || 1)) * dir;
        }

        return 0;
    });
};

export const useOrderAddItemsListsTableItems = (
    items: CustomListItem[],
    itemSearch: string,
    categoryFilter: string,
) => {
    const categories = useSelector(getLocalCategoriesList);
    const [sortKey, setSortKey] = useState<OrderAddItemsListsSortKey>('item');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const tableItems = useMemo(
        () =>
            filterAndSortListItems(
                items,
                itemSearch,
                categoryFilter,
                sortKey,
                sortDir,
                categories,
            ),
        [categories, categoryFilter, itemSearch, items, sortDir, sortKey],
    );

    const onSort = useCallback(
        (key: OrderAddItemsListsSortKey) => {
            if (sortKey === key) {
                setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortKey(key);
            setSortDir('asc');
        },
        [sortKey],
    );

    return { tableItems, sortKey, sortDir, onSort };
};
